import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import styles from "./atlas-checklist-item.scss";
import type { CheckListStatusType, CheckListStatusIconType } from "./types";
import type { Theme } from "@/internals/theme";

import "@/components/display/atlas-icon/atlas-icon";
import "@/components/display/atlas-text/atlas-text";

export type ChecklistItemProps = {
    status: string;
};

/**
 * @dependency atlas-icon
 * @dependency atlas-text
 *
 * @slot - Usado para escrever o que o item representa
 *
 * @prop {string} status - Propriedade que define o status do item
 *
 * @tag atlas-checklist-item
 */
@customElement("atlas-checklist-item")
export default class AtlasChecklistItem extends LitElement {
    static styles = styles;

    /** Define o status do item */
    @property({ type: String }) status: CheckListStatusType = "default";

    private getStatusInfo(): { theme: Theme; icon: CheckListStatusIconType } {
        const statusDetails: Record<CheckListStatusType, { theme: Theme; icon: CheckListStatusIconType }> = {
            default: { theme: "secondary", icon: "circle" },
            success: { theme: "success", icon: "check-circle" },
            warning: { theme: "warning", icon: "alert-triangle" },
            error: { theme: "danger", icon: "x" },
            info: { theme: "info", icon: "info" }
        };

        return statusDetails[this.status] || statusDetails.default;
    }

    /**
     * @internal
     */
    public render() {
        const { theme, icon } = this.getStatusInfo();

        const statusClass = {
            "atlas-checklist-item": true,
            [`${theme}`]: this.status
        };

        return html`
            <div class="${classMap(statusClass)}" role="listitem">
                <atlas-icon size="2x" theme="${theme}" name="${icon}"></atlas-icon>
                <atlas-text theme="${theme}">
                    <slot></slot>
                </atlas-text>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-checklist-item": AtlasChecklistItem;
    }
}
